import { useState, useRef } from 'react';
import { API, Storage } from '../services/amplifyCompat';
import { useOutletContext, useParams } from 'react-router-dom';
import { useGetCurrentUserProfile } from './use-get-current-user-profile.hook';
import {
  createSubmittedAction,
  createSubmittedActionItems,
} from '../graphql/mutations';
import { useGetItemsForAction } from './use-get-items-for-action';
import { useGetUserAllActions } from './use-get-user-all-actions';
import { ACTION_POINTS_VALUES } from '../constants/action-points-values';

export const useCreateUserAction = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const inFlightRef = useRef(false);

  const { actionId } = useParams();
  const { userProfile } = useGetCurrentUserProfile();
  const { userSubmittedActions, fetchAllUserSubmittedActions } =
    useGetUserAllActions();
  const { userActionValues, setUserActionValues } = useOutletContext();
  const { actionItems, hasNoItems } = useGetItemsForAction();
  const [selectedImageUrl, setSelectedImageUrl] = useState(null);

  const getActionItemValues = () => {
    if (hasNoItems) return [];
    return actionItems.map((item) => ({
      item_name: item.name_en || item.name,
      input_value:
        userActionValues?.actionItems.find(({ id }) => id === item.id)?.value ||
        '0',
    }));
  };

  const getActionItemsSum = () => {
    let co2Saved = 0;
    let pointsEarned = ACTION_POINTS_VALUES.ACTION;

    if (actionItems.length > 0) {
      actionItems.forEach((item) => {
        const userValue =
          +userActionValues?.actionItems.find(({ id }) => id === item.id)
            ?.value || 0;
        const itemCo2 = Number(item.co2Saved) || 0;

        co2Saved += userValue * itemCo2;
        pointsEarned +=
          userValue *
          (itemCo2 !== 0
            ? itemCo2 / 100
            : ACTION_POINTS_VALUES.ACTION_ITEM);
      });
    }

    return {
      co2Saved,
      pointsEarned: Math.ceil(pointsEarned),
    };
  };

  const createUserAction = async (onSuccess = () => {}) => {
    if (inFlightRef.current || loading) return;
    inFlightRef.current = true;
    setLoading(true);
    setError(null);

    const actionItemValues = getActionItemValues();
    const actionItemsSum = getActionItemsSum();
    const todayActions = userSubmittedActions.filter(
      (action) =>
        action.timeSubmitted &&
        new Date(action.timeSubmitted).toDateString() ===
          new Date().toDateString()
    );

    try {
      // All of these static values are required to be passed
      // on the initial request along with the actual values
      const variables = {
        action_id: actionId,
        date_of_action: userActionValues?.date,
        first_quiz_answer_correct: false,
        g_co2_saved: actionItemsSum?.co2Saved,
        is_validated:
          todayActions.length < 4 && actionItemsSum?.pointsEarned <= 100
            ? true
            : false,
        points_earned: actionItemsSum?.pointsEarned,
        quiz_answered: false,
        user_id: userProfile?.id,
      };
      const response = await API.graphql({
        query: createSubmittedAction,
        variables,
      });

      const submittedAction = await response.data.createSubmittedAction;
      const userActionId = submittedAction?.sa_id;

      setUserActionValues({
        ...userActionValues,
        pointsEarned: actionItemsSum?.pointsEarned,
        userActionId,
      });

      if (actionItemValues.length > 0) {
        try {
          await API.graphql({
            query: createSubmittedActionItems,
            variables: {
              sa_id: userActionId,
              submitted_action_items: actionItemValues,
            },
          });
        } catch (e) {
          console.log('Error creating action items:', e);
          // Non-fatal — action was created; continue
        }
      }

      if (userActionValues?.image) {
        try {
          let imageType = userActionValues?.image.type;
          let imageKey = 'validation/input/'.concat(
            userActionId,
            '.',
            imageType.split('/')[1]
          );

          const uploadedImage = await Storage.put(
            imageKey,
            userActionValues?.image,
            {
              contentType: imageType,
            }
          );

          const imageUrl = uploadedImage.url;
          setSelectedImageUrl(imageUrl);
        } catch (imageError) {
          // Image upload failed but action was created — still proceed
          // so the user is not stuck and does not create a duplicate action
          console.log('Image upload failed:', imageError);
        }
      }

      fetchAllUserSubmittedActions();
      setLoading(false);
      inFlightRef.current = false;
      onSuccess();
    } catch (e) {
      setLoading(false);
      inFlightRef.current = false;
      setError(e);
      console.log('Error: ', e);
    }
  };

  return {
    createUserAction,
    loading,
    error,
    selectedImageUrl,
  };
};
